import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../../api/apiServices';
import { sanitizeInput } from '../../util/helpers';
import { FaCheckCircle, FaTimesCircle, FaEye, FaEyeSlash } from 'react-icons/fa';

const RegisterForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordRules, setPasswordRules] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasPunctuation: false,
  });
  const [error, setError] = useState('');
  const [userExistsError, setUserExistsError] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState('');
  const [sanitizeError, setSanitizeError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const navigate = useNavigate();

  const validatePassword = (password: string) => {
    const rules = {
      minLength: password.length >= 6,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasPunctuation: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    setPasswordRules(rules);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setUserExistsError(false);

    const sanitizedEmail = sanitizeInput(email);
    const sanitizedUsername = sanitizeInput(username);
    const sanitizedPassword = sanitizeInput(password);
    const sanitizedConfirmPassword = sanitizeInput(confirmPassword);

    if (
      sanitizedEmail !== email ||
      sanitizedUsername !== username ||
      sanitizedPassword !== password ||
      sanitizedConfirmPassword !== confirmPassword
    ) {
      setSanitizeError('Your input contains invalid characters.');
      return;
    }

    if (password !== confirmPassword) {
      setPasswordMatchError('Passwords do not match.');
      return;
    }

    if (!Object.values(passwordRules).every(Boolean)) {
      setError('Password does not meet all security requirements.');
      return;
    }

    try {
      await registerUser({ email: sanitizedEmail, username: sanitizedUsername, password: sanitizedPassword });
      setRegistrationSuccess(true);
    } catch (err: any) {
      if (err.response?.data?.message === 'User already exists') {
        setUserExistsError(true);
      } else {
        setError('Registration failed. Please try again.');
      }
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white shadow-md rounded-lg space-y-6">
      <h2 className="text-2xl font-bold mb-4">Create an Account</h2>

      {registrationSuccess ? (
        <div className="text-center text-green-600">
          ✅ A confirmation email has been sent to <strong>{email}</strong>. Please check your inbox and verify your email to activate your account.
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>

          {/* Password Input with Eye Toggle */}
          <div className="mb-4 relative">
            <label className="block text-gray-700">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  validatePassword(e.target.value);
                }}
                placeholder="Enter your password"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
              </button>
            </div>

            {/* Password Strength Checklist */}
            <div className="mt-2 text-sm text-gray-500">Password must meet the following requirements:</div>
            <ul className="mt-2 space-y-1">
              {Object.entries(passwordRules).map(([rule, isValid]) => (
                <li key={rule} className={`flex items-center ${isValid ? 'text-green-600' : 'text-red-500'}`}>
                  {isValid ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
                  {rule === 'minLength' && 'At least 6 characters'}
                  {rule === 'hasUpperCase' && 'At least 1 uppercase letter'}
                  {rule === 'hasLowerCase' && 'At least 1 lowercase letter'}
                  {rule === 'hasNumber' && 'At least 1 number'}
                  {rule === 'hasPunctuation' && 'At least 1 punctuation mark'}
                </li>
              ))}
            </ul>
          </div>

          {/* Confirm Password Input with Eye Toggle */}
          <div className="mb-4 relative">
            <label className="block text-gray-700">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
              </button>
            </div>
            {passwordMatchError && <div className="mt-2 text-red-500">{passwordMatchError}</div>}
          </div>

          {sanitizeError && <div className="mt-2 text-red-500">{sanitizeError}</div>}
          {userExistsError && (
            <div className="my-2 text-yellow-500">
              ⚠ This email is already registered. Try logging in instead.
              <button
                onClick={() => navigate('/login')}
                className="text-blue-600 hover:underline ml-1"
              >
                Login here
              </button>
            </div>
          )}
          <button
            type="submit"
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Register
          </button>
          {error && <div className="mt-2 text-red-500">{error}</div>}
        </form>
      )}
    </div>
  );
};

export default RegisterForm;
